import { Preferences } from "@capacitor/preferences";
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonModal, IonText, IonTitle, IonToolbar } from "@ionic/react";
import { DropdownSelectItem } from "components/DropdownSelect";
import { Glob, IsLanguageCode, langNames, LanguageCode, languages, useLoc } from "functions/Language";
import { FC, useEffect, useState } from "react";

import cfg from "config.json";
import { colorPaletteOutline, informationCircleOutline, languageOutline, refreshOutline, settingsOutline } from "ionicons/icons";


export const defaultMqttBroker = "mqtts://mqtt.bmr.cz:5281/mqtt";

export type PrefKey = "lang" | "theme" | "refresh" | "mqtt-broker" | "users" | "selUser" | `overviewChart${number}`;
export const themes = ["light", "dark"] as const;

const refreshOptions = [1, 5, 30, -1];

// stare verze chybne ukladaly nastaveni mimo skupinu
export async function LoadPreference(key: PrefKey) {
  await Preferences.configure({ group: cfg.pref_group });
  let value = (await Preferences.get({ key })).value;

  let legacy_value = null;
  if (null === value) {
    await Preferences.configure({ group: "CapacitorStorage" });
    legacy_value = (await Preferences.get({ key })).value;
  }

  if (null !== legacy_value) {
    await Preferences.configure({ group: cfg.pref_group });
    try {
      await Preferences.set({ key, value: legacy_value });
      await Preferences.configure({ group: "CapacitorStorage" });
      await Preferences.remove({ key });
    } catch {}
  }

  return value ?? legacy_value;
}

// stare verze chybne ukladaly nastaveni mimo skupinu
export async function SavePreference(key: PrefKey, value: string | null) {
    await Preferences.configure({ group: cfg.pref_group });
    if (null === value) {
      await Preferences.remove({ key });
    }
    else {
      await Preferences.set({ key, value });
    }

    try {
      await Preferences.configure({ group: "CapacitorStorage" });
      await Preferences.remove({ key });
    } catch {}
}


export let ReloadSettings = { callback: () => {console.warn("Reloading settings too soon");} };
export async function LoadSettings() {
  let lng = await LoadPreference("lang");
  let theme = await LoadPreference("theme");
  let refresh_ = await LoadPreference("refresh");
  let refresh = Number.parseFloat(refresh_ ?? "");

  return {
    language: IsLanguageCode(lng) ? lng : "cs",
    theme: (themes.some(v => v === theme) ? theme : "light") as typeof themes[number],
    refresh: isNaN(refresh) ? 5 : refresh,
  }
}

export const AppSettings : FC = () => {
  const loc = useLoc(glob);
  const [isOpen, setOpen] = useState(false);
  const [langid, setLangid] = useState(1);
  const [, setLang] = useState<LanguageCode>("cs");
  const [theme, setTheme] = useState(0);
  const [refreshId, setRefreshId] = useState(0);

  useEffect(() => {
    (async () => {
      let conf = await LoadSettings();

      setLang(conf.language);
      let langid = languages.indexOf(conf.language);
      if (langid < 0)
        langid = 0;
      setLangid(langid)

      let rid = refreshOptions.indexOf(conf.refresh);
      if (rid < 0)
        rid = 0;
      setRefreshId(rid);

      let themeid = themes.indexOf(conf.theme);
      if (themeid < 0)
        themeid = 0;
      setTheme(themeid);
      switch (conf.theme) {
        case 'dark':
          document.body.classList.add("dark");
          break;
        case 'light':
          document.body.classList.remove("dark");
      }
    })();
  }, []);

  return <>
    <IonButton onClick={() => setOpen(true)}>
      <IonIcon icon={settingsOutline} />
    </IonButton>
    <IonModal isOpen={isOpen} onWillDismiss={() => setOpen(false)}>
      <IonHeader>
        <IonToolbar color="tertiary">
          <IonTitle>{loc.title}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setOpen(false)}>{loc.close}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <DropdownSelectItem
          label="Language"
          options={langNames}
          selected={langid}
          setSelected={id => {
            setLangid(id);
            SavePreference("lang", languages[id])
            .then(ReloadSettings.callback);
          }}
          icon={languageOutline}
        />

        <DropdownSelectItem
          label={loc.theme}
          options={themes.map(v => loc[`theme_${v}`])}
          selected={theme}
          setSelected={id => {
            setTheme(id);
            SavePreference("theme", themes[id])
            .then(ReloadSettings.callback);
          }}
          icon={colorPaletteOutline}
        />

        <DropdownSelectItem
          label={loc.refresh}
          options={refreshOptions.map(v => v < 0 ? loc.refreshnt : `${v} s`)}
          selected={refreshId}
          setSelected={id => {
            setRefreshId(id);
            SavePreference("refresh", refreshOptions[id].toString())
            .then(ReloadSettings.callback);
          }}
          icon={refreshOutline}
        />

        <IonItem>
          <IonIcon icon={informationCircleOutline} />
          <IonLabel>&nbsp;{loc.version}</IonLabel>
          <IonText color="medium">{cfg.version}</IonText>
        </IonItem>
      </IonContent>
    </IonModal>
  </>
}

const glob = Glob({
	en: {
		close: "Close",
		title: "Application settings",
		theme: "Theme",
		theme_light: "Light",
		theme_dark: "Dark",
		version: "Application version",
		refresh: "Refreshing period",
		refreshnt: "Do not refresh",
	},
	cs : {
		close: "Zavřít",
		title: "Nastavení aplikace",
		theme: "Motiv",
		theme_light: "Světlý",
		theme_dark: "Tmavý",
		version: "Verze aplikace",
		refresh: "Interval načítání",
		refreshnt: "Nenačítat",
	},
	pg: {
		close: "Fechar",
		title: "Configurações do aplicativo",
		theme: "Tema de cores",
		theme_light: "Leve",
		theme_dark: "Escuro",
		version: "Versão do aplicativo",
		refresh: "Intervalo de atualização",
		refreshnt: "Não atualizar",
	},
	es: {
		close: "Cerrar",
		title: "Configuración de la aplicación",
		theme: "Tema de color",
		theme_light: "Luminoso",
		theme_dark: "Oscuro",
		version: "Versión de la aplicación",
		refresh: "Intervalo de actualización",
		refreshnt: "No actualizar",
	}
});
